/**
 * Comptes bancaires et financements d'une entreprise : forme unique (celle du
 * DTO customer-service : BankAccountDto, FinancingDto), lecture tolérante des
 * anciennes formes, et partage du tableau `financements` entre la section
 * « prêts » et la section « levées de fonds ».
 *
 * Le serveur n'a qu'un tableau `financements` ; c'est `typeFinancement` qui
 * distingue un prêt d'une levée de fonds. Chaque section n'édite que sa part
 * et la fusion conserve l'autre, pour qu'enregistrer l'une n'efface jamais
 * l'autre.
 */
import type { BankAccount, Financing } from '../types/company';

export type Devise = BankAccount['devise'];
export type TypeCompte = NonNullable<BankAccount['typeCompte']>;
export type TypeFinancement = Financing['typeFinancement'];
export type TypeLevee = NonNullable<Financing['typeLevee']>;
export type FinancingSection = 'loans' | 'funding-rounds';

export const DEVISES: ReadonlyArray<{ value: Devise; label: string }> = [
  { value: 'USD', label: 'Dollar US ($)' },
  { value: 'CDF', label: 'Franc congolais (FC)' },
  { value: 'EUR', label: 'Euro (€)' },
];

export const TYPES_COMPTE: ReadonlyArray<{ value: TypeCompte; label: string }> = [
  { value: 'courant', label: 'Compte courant' },
  { value: 'epargne', label: 'Compte épargne' },
  { value: 'professionnel', label: 'Compte professionnel' },
  { value: 'devise', label: 'Compte en devises' },
];

export const TYPES_FINANCEMENT: ReadonlyArray<{ value: TypeFinancement; label: string }> = [
  { value: 'pret_bancaire', label: 'Prêt bancaire' },
  { value: 'credit_bail', label: 'Crédit-bail, leasing' },
  { value: 'crowdfunding', label: 'Financement participatif' },
  { value: 'investissement_capital', label: 'Investissement en capital' },
  { value: 'subventions', label: "Subventions, programmes d'accompagnement" },
  { value: 'financement_public', label: 'Financement public ou institutionnel' },
  { value: 'autre', label: 'Autre' },
];

export const TYPES_LEVEE_TOUR: ReadonlyArray<{ value: TypeLevee; label: string }> = [
  { value: 'pre_amorcage', label: 'Pré-amorçage' },
  { value: 'amorcage', label: 'Amorçage' },
  { value: 'serie_a', label: 'Série A' },
  { value: 'serie_b', label: 'Série B' },
  { value: 'serie_c', label: 'Série C' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'autre', label: 'Autre' },
];

/** Types qui relèvent d'une levée de fonds (section réservée aux startups). */
const TYPES_LEVEE: ReadonlySet<TypeFinancement> = new Set<TypeFinancement>([
  'crowdfunding',
  'investissement_capital',
  'subventions',
  'financement_public',
]);

export const isLeveeDeFonds = (f: Financing): boolean => TYPES_LEVEE.has(f.typeFinancement);

/**
 * Types proposés dans une section. Sans section « levées de fonds » (entreprise
 * traditionnelle), la section prêts couvre tous les types.
 */
export function typesForSection(section: FinancingSection, isStartup: boolean) {
  if (section === 'funding-rounds') return TYPES_FINANCEMENT.filter((t) => TYPES_LEVEE.has(t.value));
  return isStartup ? TYPES_FINANCEMENT.filter((t) => !TYPES_LEVEE.has(t.value)) : TYPES_FINANCEMENT;
}

/** Part du tableau `financements` qu'une section affiche et édite. */
export function itemsForSection(all: Financing[], section: FinancingSection, isStartup: boolean): Financing[] {
  if (section === 'funding-rounds') return all.filter(isLeveeDeFonds);
  return isStartup ? all.filter((f) => !isLeveeDeFonds(f)) : all;
}

/**
 * Remplace la part d'une section dans `financements` et garde le reste tel
 * quel : enregistrer les prêts ne touche pas aux levées, et inversement.
 */
export function mergeFinancingSection(
  all: Financing[],
  section: FinancingSection,
  sectionItems: Financing[],
  isStartup: boolean,
): Financing[] {
  const managed = new Set(itemsForSection(all, section, isStartup).map((f) => f.id));
  return [...all.filter((f) => !managed.has(f.id)), ...sectionItems];
}

// ── Lecture tolérante ───────────────────────────────────────────────────────
// Les enregistrements écrits avant l'alignement sur le DTO (serveur ou
// brouillon localStorage) ont des champs anglais : on les relit sans perte.

type Raw = Record<string, unknown>;

const isRaw = (v: unknown): v is Raw => v !== null && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
const optStr = (v: unknown): string | undefined => str(v) || undefined;
const num = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
};
const pick = (raw: Raw, ...keys: string[]): unknown => keys.map((k) => raw[k]).find((v) => v !== undefined && v !== null && v !== '');

const devise = (v: unknown): Devise => (v === 'CDF' || v === 'EUR' ? v : 'USD');

const LEGACY_TYPE_COMPTE: Record<string, TypeCompte> = {
  checking: 'courant',
  savings: 'epargne',
  business: 'professionnel',
};
const typeCompte = (v: unknown): TypeCompte | undefined => {
  const s = str(v);
  if (TYPES_COMPTE.some((t) => t.value === s)) return s as TypeCompte;
  return LEGACY_TYPE_COMPTE[s];
};

const typeFinancement = (v: unknown): TypeFinancement | undefined => {
  const s = str(v);
  return TYPES_FINANCEMENT.find((t) => t.value === s)?.value;
};

const LEGACY_TYPE_LEVEE: Record<string, TypeLevee> = {
  'pre-seed': 'pre_amorcage',
  seed: 'amorcage',
  'series-a': 'serie_a',
  'series-b': 'serie_b',
  'series-c': 'serie_c',
  bridge: 'bridge',
};
const typeLevee = (v: unknown): TypeLevee | undefined => {
  const s = str(v);
  return TYPES_LEVEE_TOUR.find((t) => t.value === s)?.value ?? LEGACY_TYPE_LEVEE[s];
};

let sequence = 0;
const fallbackId = (prefix: string) => `${prefix}_${Date.now()}_${++sequence}`;

export function normalizeBankAccount(value: unknown): BankAccount | null {
  if (!isRaw(value)) return null;
  const account: BankAccount = {
    id: str(value.id) || fallbackId('bank'),
    nomBanque: str(pick(value, 'nomBanque', 'bankName', 'banque')),
    intituleCompte: str(pick(value, 'intituleCompte', 'accountTitle')),
    numeroCompte: str(pick(value, 'numeroCompte', 'accountNumber')),
    devise: devise(pick(value, 'devise', 'currency')),
  };
  const type = typeCompte(pick(value, 'typeCompte', 'accountType'));
  if (type) account.typeCompte = type;
  for (const key of ['agence', 'codeSwift', 'iban'] as const) {
    const v = optStr(value[key]);
    if (v) account[key] = v;
  }
  if (typeof value.estPrincipal === 'boolean') account.estPrincipal = value.estPrincipal;
  return account;
}

export function normalizeFinancing(value: unknown): Financing | null {
  if (!isRaw(value)) return null;
  // Ancien « tour de financement » : type de tour, montant levé, investisseurs.
  const isLegacyRound = value.roundType !== undefined || value.amountRaised !== undefined;
  const investors = Array.isArray(value.investors) ? value.investors.map(str).filter(Boolean).join(', ') : '';

  const financing: Financing = {
    id: str(value.id) || fallbackId(isLegacyRound ? 'round' : 'loan'),
    typeFinancement:
      typeFinancement(value.typeFinancement) ?? (isLegacyRound ? 'investissement_capital' : 'pret_bancaire'),
    organisme: str(pick(value, 'organisme', 'lender')) || investors,
    montantTotal: num(pick(value, 'montantTotal', 'totalAmount', 'amountRaised')) ?? 0,
    devise: devise(pick(value, 'devise', 'currency')),
  };
  const objet = str(pick(value, 'objetFinancement', 'purpose', 'useOfFunds'));
  if (objet) financing.objetFinancement = objet;

  const montantRestant = num(pick(value, 'montantRestant', 'outstandingAmount'));
  if (montantRestant !== undefined) financing.montantRestant = montantRestant;
  const tauxInteret = num(pick(value, 'tauxInteret', 'interestRate'));
  if (tauxInteret !== undefined) financing.tauxInteret = tauxInteret;
  const mensualite = num(pick(value, 'mensualite', 'monthlyPayment'));
  if (mensualite !== undefined) financing.mensualite = mensualite;
  const dateObtention = optStr(pick(value, 'dateObtention', 'startDate', 'date'));
  if (dateObtention) financing.dateObtention = dateObtention;
  const dateEcheance = optStr(pick(value, 'dateEcheance', 'endDate'));
  if (dateEcheance) financing.dateEcheance = dateEcheance;
  const garanties = optStr(value.garanties);
  if (garanties) financing.garanties = garanties;
  const tour = typeLevee(pick(value, 'typeLevee', 'roundType'));
  if (tour) financing.typeLevee = tour;
  const preMoney = num(pick(value, 'valorisationPreMoney', 'preMoney'));
  if (preMoney !== undefined) financing.valorisationPreMoney = preMoney;
  const postMoney = num(pick(value, 'valorisationPostMoney', 'postMoney'));
  if (postMoney !== undefined) financing.valorisationPostMoney = postMoney;
  const statut = str(value.statut);
  if (statut === 'en_cours' || statut === 'rembourse' || statut === 'defaut' || statut === 'restructure') {
    financing.statut = statut;
  }
  return financing;
}

const normalizeList = <T>(value: unknown, one: (v: unknown) => T | null): T[] =>
  Array.isArray(value) ? value.map(one).filter((v): v is T => v !== null) : [];

export const normalizeBankAccounts = (value: unknown): BankAccount[] => normalizeList(value, normalizeBankAccount);
export const normalizeFinancings = (value: unknown): Financing[] => normalizeList(value, normalizeFinancing);
